<template>
  <div class="edit-content my-5">
    <div class="container">
      <div class="row mb-3">
        <div class="col-auto">
          <div class="form-group">
            <fieldset :disabled="saving">
              <select name="selectedEvent" class="form-control" v-model="selectedEvent" @change="selectedEventChanged">
                <option :value="null">Select event</option>
                <option :value="event.id" v-for="event in events" :key="`eventSelect-${event.id}`">{{ event.header }}</option>
              </select>
            </fieldset>
          </div>
        </div>
      </div>

      <div class="row" v-if="currentEvent" :key="`${selectedEvent}${event.updatedAt}`">
        <div class="col-6 mb-5">
          <strong class="d-block">Set event mode</strong>

          <div class="btn-group">
            <button type="button" class="btn btn-lg text-capitalize" :class="{ 'btn-success': currentEvent.mode == mode, 'btn-outline-dark': currentEvent.mode != mode }" v-for="mode in modes" :key="`set-mode-${mode}`" @click="setMode(mode)">{{ mode }}</button>
          </div>
          <div class="form-text text-muted">Will update immediately and any unsaved changes will be lost.</div>
        </div>
        
        <div class="col-6 mb-5">
          <strong class="d-block">Preview event mode</strong>

          <div class="d-flex">
            <a :href="`/event/${event.id}?wearejaws=${mode}`" target="_blank" class="flex-grow-1 text-capitalize mr-1" v-for="mode in modes" :key="`preview-link-${mode}`">{{ mode }}</a>
          </div>
        </div>

        <div class="col-12">
          <form name="form" @submit.prevent="updateContent">
            <fieldset :disabled="saving">
              <div class="form-group">
                <label for="header">Header</label>
                <input type="text" name="header" id="header" v-model="currentEvent.header" class="form-control">
              </div>

              <div class="form-group">
                <label for="subHeader">Sub header</label>
                <input type="text" name="subHeader" id="subHeader" v-model="currentEvent.subHeader" class="form-control">
              </div>

              <div class="form-group">
                <label for="eventStart">Event start</label>
                <input type="text" name="eventStart" id="eventStart" v-model="currentEvent.eventStart" class="form-control">
              </div>

              <div class="form-group">
                <label for="eventEnd">Event end</label>
                <input type="text" name="eventEnd" id="eventEnd" v-model="currentEvent.eventEnd" class="form-control">
              </div>

              <div class="form-group">
                <label for="streamUrl">Stream URL</label>
                <input type="text" name="streamUrl" id="streamUrl" v-model="currentEvent.streamUrl" class="form-control">
              </div>

              <div class="form-group">
                <label for="ondemandUrl">On-demand URL</label>
                <input type="text" name="ondemandUrl" id="ondemandUrl" v-model="currentEvent.ondemandUrl" class="form-control">
              </div>

              <div class="form-group">
                <label for="ondemandUrl" class="d-block">Enable QnA?</label>
                <div class="form-check form-check-inline">
                  <input type="radio" name="qnaEnabled" id="qnaEnabledTrue" class="form-check-input" v-model="currentEvent.qnaEnabled" :value="true">
                  <label for="qnaEnabledTrue" class="form-check-label">Yes</label>
                </div>
                <div class="form-check form-check-inline">
                  <input type="radio" name="qnaEnabled" id="qnaEnabledFalse" class="form-check-input" v-model="currentEvent.qnaEnabled" :value="false">
                  <label for="qnaEnabledFalse" class="form-check-label">No</label>
                </div>
              </div>

              <div class="form-group">
                <label for="image">Image</label>
                <input type="text" name="image" id="image" v-model="currentEvent.image" class="form-control">
              </div>

              <div class="form-group">
                <label for="contentDefault">Content - Default</label>
                <editor v-model="currentEvent.contentDefault"></editor>
              </div>

              <div class="form-group">
                <label for="contentSignedIn">Content - Signed in</label>
                <editor v-model="currentEvent.contentSignedIn"></editor>
              </div>

              <div class="form-group">
                <label for="contentAgenda">Agenda</label>
                <editor v-model="currentEvent.contentAgenda"></editor>
              </div>

              <button type="submit" name="submit" class="btn btn-primary">Save</button>
              <span class="d-inline-block ml-3 text-success" v-if="message">{{ message }}</span>
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Editor from "@/components/editor"
  import { firebase } from "@/initializers/firebase"

  export default {
    name: "edit-content",
    components: { Editor },
    beforeRouteEnter(to, from, next) {
      if(to.query.showme) {
        next()
      } else {
        next({ name: "home" })
      }
    },
    data() {
      return {
        loading: true,
        saving: false,
        message: null,
        events: [],
        selectedEvent: null,
        currentEvent: null,
        modes: [ "offline", "pre", "live", "post", "ondemand" ]
      }
    },
    computed: {
      event() {
        return this.events.find((event) => event.id == this.selectedEvent)
      },
      eventStartAsISOString() {
        return this.currentEvent.eventStart.toDate()
      },
      contentDefaultEditor() {
        return new Editor({
          content: this.currentEvent.contentDefault
        })
      }
    },
    methods: {
      selectedEventChanged() {
        let event = this.events.find((event) => event.id == this.selectedEvent)
        this.setCurrentEvent(event)
      },
      setCurrentEvent(event) {
        let object = {}
        let startDateISO = this.event.eventStart.toDate().toISOString()
        object.eventStart = startDateISO

        if(this.event.eventEnd) {
          let endDateISO = this.event.eventEnd.toDate().toISOString()
          object.eventEnd = endDateISO
        }

        this.currentEvent = Object.assign({}, event, object)
      },
      async updateContent() {
        this.saving = true

        try {
          let event = Object.assign({}, this.currentEvent)
          let eventStartDate = new Date(event.eventStart)
          let eventEndDate = new Date(event.eventEnd)

          let eventStartTimestamp = await firebase.firestore.Timestamp.fromDate(eventStartDate)
          event.eventStart = eventStartTimestamp

          if(eventEndDate == "Invalid Date") {
            event.eventEnd = firebase.firestore.FieldValue.delete()
          } else {
            let eventEndTimestamp = await firebase.firestore.Timestamp.fromDate(eventEndDate)
            event.eventEnd = eventEndTimestamp
          }

          event.updatedAt = firebase.firestore.FieldValue.serverTimestamp()

          let eventRef = await firebase.firestore().collection(`events`).doc(this.selectedEvent)
          await eventRef.update(event)
          
          this.message = "Updated" 
          this.saving = false
        } catch(error) {
          this.message = error
          this.saving = false
        }

        setTimeout(() => {
          this.message = null
        }, 2500)
      },
      async setMode(mode) {
        let eventRef = await firebase.firestore().collection(`events`).doc(this.selectedEvent)
        await eventRef.update({ mode: mode, updatedAt: firebase.firestore.FieldValue.serverTimestamp() })

        this.setCurrentEvent(this.event)
      }
    },
    async mounted() {
      await this.$bind("events", firebase.firestore().collection(`events`).orderBy("eventStart"))
      this.loading = false
    }
  }
</script>